import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import {
  ChainlitContext,
  messagesState,
  threadHistoryState,
  useChatInteract,
  useChatMessages
} from '@chainlit/react-client';

import {
  attachmentsState,
  documentContextEnabledState,
  persistentCommandState,
  snowAttachmentFlowState,
  webSearchEnabledState
} from 'state/chat';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from '@/components/ui/sidebar';

interface ClearConversationsProps {
  disabled?: boolean;
}

export default function ClearConversations({ disabled = false }: ClearConversationsProps) {
  const navigate = useNavigate();
  const apiClient = useContext(ChainlitContext);
  const { clear } = useChatInteract();
  const { threadId: currentThreadId } = useChatMessages();
  const setThreadHistory = useSetRecoilState(threadHistoryState);
  const [isDeleting, setIsDeleting] = useState(false);

  // Additional state setters for local chat states
  const setMessages = useSetRecoilState(messagesState);
  const setAttachments = useSetRecoilState(attachmentsState);
  const setDocumentContextEnabled = useSetRecoilState(documentContextEnabledState);
  const setPersistentCommand = useSetRecoilState(persistentCommandState);
  const setSnowAttachmentFlow = useSetRecoilState(snowAttachmentFlowState);
  const setWebSearchEnabled = useSetRecoilState(webSearchEnabledState);

  const handleClearAll = async () => {
    if (isDeleting) return;

    setIsDeleting(true);

    try {
      // Get current thread history to know which threads to delete
      const { data: threads } = await apiClient.listThreads(
        { first: 1000 }, // Get a large number to capture all threads
        {}
      );

      if (!threads || threads.length === 0) {
        toast.success('No conversations to clear');
        return;
      }

      // Show progress toast - delete in batches to avoid overwhelming the server
      const deletePromise = (async () => {
        const batchSize = 10; // Process 10 deletions at a time
        const batches = [];
        
        for (let i = 0; i < threads.length; i += batchSize) {
          const batch = threads.slice(i, i + batchSize);
          batches.push(batch);
        }
        
        // Process batches sequentially, but threads within each batch in parallel
        for (const batch of batches) {
          await Promise.all(
            batch.map(thread => apiClient.deleteThread(thread.id))
          );
        }
      })();

      toast.promise(deletePromise, {
        loading: "Clearing conversations...",
        success: () => {
          // Clear the thread history state
          setThreadHistory((prev) => ({
            ...prev,
            threads: []
          }));
          
          // Clear the current chat session and reset all local states
          // This ensures we return to the welcome screen
          clear();
          
          // Explicitly clear messages to ensure welcome screen shows
          setMessages([]);
          
          // Reset additional local chat states
          setAttachments([]);
          setDocumentContextEnabled(false);
          setPersistentCommand(undefined);
          setSnowAttachmentFlow(false);
          setWebSearchEnabled(false);
          
          // Navigate to home with replace to clear history and ensure clean state
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 200);
          
          return "All conversations cleared";
        },
        error: (err) => {
          console.error('Error clearing conversations:', err);
          return 'Failed to clear all conversations';
        }
      });

      await deletePromise;
    } catch (error) {
      console.error('Error clearing conversations:', error);
      toast.error('Failed to clear conversations');
    } finally {
      setIsDeleting(false);
    }
  };  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <SidebarMenuButton
              size="sm"
              disabled={disabled || isDeleting}
              className="w-full flex items-center gap-2 text-[#152644] hover:text-[#152644] hover:bg-[#152644]/10 justify-start"
            >
              <Trash2 className="!size-4" />
              Clear Conversations
            </SidebarMenuButton>
          </AlertDialogTrigger>

          <AlertDialogContent><AlertDialogHeader>
          <AlertDialogTitle>
            Clear All Conversations
          </AlertDialogTitle>
          <AlertDialogDescription>
            This will delete all your conversations and cannot be undone. Are you sure you want to continue?
          </AlertDialogDescription>
        </AlertDialogHeader>        <AlertDialogFooter className="flex-row gap-2 sm:gap-0">
          <AlertDialogCancel className="mt-0">
            Cancel
          </AlertDialogCancel>          <AlertDialogAction 
            onClick={handleClearAll}
            disabled={isDeleting}
            className="bg-[#152644] text-white hover:bg-[#152644]/90"
          >
            Confirm
          </AlertDialogAction>        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </SidebarMenuItem>
</SidebarMenu>
  );
}
